import { useRef, useState } from 'react'

import { EMPTY_STRING } from '../../../../../../../constants/common'
import {
  EVENT_NAME_KEY_DOWN,
  EVENT_NAME_MOUSE_DOWN,
} from '../../../../../../../constants/event'
import { useEventListener, useNotification } from '../../../../../../../hooks'
import { isEventKeyEscape } from '../../../../../../../utils/events'
import { patchMockApplicant } from '../../../../../../RentalPortalApplicant'

export function useApplicantProfileNoteEdit({
  applicantId,
  handleEntityReload,
  value,
}) {
  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [isEditing, setEditing] = useState(false)
  const [isSaving, setSaving] = useState(false)
  const [editValue, setEditValue] = useState(null)

  const editContainerRef = useRef(null)
  const isCommittingRef = useRef(false)

  function handleStartEditing() {
    setEditValue(value ?? EMPTY_STRING)
    setEditing(true)
  }

  function handleChangeEditValue(nextValue) {
    setEditValue(nextValue)
  }

  async function saveEditValue() {
    const content = editValue.trim() || null

    setSaving(true)

    try {
      await patchMockApplicant(applicantId, { content })
      await handleEntityReload()

      handleAddSuccessNotification('Notification.CommonTitleSavedKey')

      setEditing(false)
    } catch {
      handleAddErrorNotification(
        'RentalPortal.ApplicantProfileNoteSaveErrorKey'
      )
    }

    setSaving(false)
    isCommittingRef.current = false
  }

  async function handleSaveEditValue() {
    if (!isCommittingRef.current) {
      isCommittingRef.current = true

      const isUnchanged = editValue.trim() === (value ?? EMPTY_STRING).trim()

      if (isUnchanged) {
        setEditing(false)
        isCommittingRef.current = false
      } else {
        await saveEditValue()
      }
    }
  }

  function handleDocumentMouseDown(event) {
    if (isEditing && !editContainerRef.current?.contains(event.target)) {
      handleSaveEditValue()
    }
  }

  function handleDocumentKeyDown(event) {
    if (isEditing && isEventKeyEscape(event)) {
      setEditing(false)
    }
  }

  useEventListener({
    eventName: EVENT_NAME_MOUSE_DOWN,
    handler: handleDocumentMouseDown,
  })

  useEventListener({
    eventName: EVENT_NAME_KEY_DOWN,
    handler: handleDocumentKeyDown,
  })

  return {
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleSaveEditValue,
    handleStartEditing,
    isEditing,
    isSaving,
  }
}
